import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Identifier, RaRecord, useChoicesContext } from 'react-admin';
import { useState, useEffect } from 'react';
import { debounce } from '@mui/material/utils';
import { CircularProgress } from '@mui/material';
import { SetPropsType } from './DataTypes';

export default function CheckboxesAutoComplete({
  setFunction,
  label,
  reset = false,
  getOptionLabel,
  optionValueKey,
}: SetPropsType) {
  const [key, setKey] = useState(0);
  const { allChoices = [], isLoading, isFetching } = useChoicesContext();
  const [options, setOptions] = useState<RaRecord<Identifier>[]>([]);

  const showAutocompleteSpinner = isLoading || isFetching;
  const effectivelyDisabled = isLoading || isFetching || (!isLoading && !isFetching && allChoices.length === 0);

  useEffect(() => {
    if (allChoices) {
      const uniqueChoices = Array.from(new Map(allChoices.map((item) => [item[optionValueKey], item])).values());
      setOptions(uniqueChoices);
    }
  }, [allChoices, optionValueKey]);

  useEffect(() => {
    if (reset) {
      setFunction([]);
      setOptions(allChoices);
      setKey((prevKey) => prevKey + 1);
    }
  }, [reset, allChoices, setFunction]);

  const handleChange = (event: any, newValues: RaRecord<Identifier>[]) => {
    setFunction(newValues);
  };

  const handleInputChange = debounce((event, newInputValue, reason: string) => {
    const lowerInput = newInputValue.toLowerCase();
    if (lowerInput.length === 0) {
      const uniqueChoices = Array.from(new Map(allChoices.map((item) => [item[optionValueKey], item])).values());
      setOptions(uniqueChoices);
      return;
    }
    const filtered = allChoices.filter((item: any) => {
      const nameMatch = item.client_name && item.client_name.toLowerCase().includes(lowerInput);
      const numberMatch = item.client_number && item.client_number.toLowerCase().includes(lowerInput);
      return nameMatch || numberMatch;
    });
    setOptions(filtered);
  }, 300);

  return (
    <Autocomplete
      limitTags={1}
      componentsProps={{
        paper: {
          sx: {
            backgroundColor: '#fcfcfa',
          },
        },
      }}
      key={key}
      multiple
      includeInputInList
      filterSelectedOptions
      disabled={effectivelyDisabled}
      loading={showAutocompleteSpinner}
      id="checkbox-autocomplete"
      options={options}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      filterOptions={(x) => x}
      onInputChange={(event, newInputValue, reason) => {
        handleInputChange(event, newInputValue, reason);
      }}
      renderOption={(props, option) => (
        <li {...props} key={`${option.client_number}-${option.client_name}`}>
          {getOptionLabel(option)}
        </li>
      )}
      isOptionEqualToValue={(option, value) => option[optionValueKey] === value[optionValueKey]}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder=""
          disabled={effectivelyDisabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showAutocompleteSpinner ? (
                  <CircularProgress color="inherit" size={20} sx={{ marginRight: '8px' }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
